import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Parse from 'parse';
import { useGetIdentity } from 'react-admin';
import CustomPagination from '../../components/CustomPagination';

/**
 * Activity Logs — Super-User only.
 *
 * One table for the whole portal, with a tab per actor role. Tabs, sections and
 * actions all come from the data, so a new module or a new action appears here
 * without a code change.
 */

/** 'operator_game_config_updated' / 'betLevels' -> 'Operator Game Config Updated' */
export const humanize = (value) => {
  if (value === null || value === undefined || value === '') return '';
  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Reduce an action to the verb an operator cares about. */
const actionVerb = (action) => {
  const normalized = String(action || '').toLowerCase();
  if (/delete|removed/.test(normalized)) return { label: 'Deleted', tone: 'danger' };
  if (/deactivat|disabled|revoked/.test(normalized)) return { label: 'Disabled', tone: 'danger' };
  if (/creat|added|registered/.test(normalized)) return { label: 'Created', tone: 'success' };
  if (/activat|enabled|granted/.test(normalized)) return { label: 'Enabled', tone: 'success' };
  if (/permission|access/.test(normalized)) return { label: 'Permissions', tone: 'info' };
  if (/upload/.test(normalized)) return { label: 'Uploaded', tone: 'info' };
  return { label: 'Updated', tone: 'warning' };
};

const TONES = {
  danger: { bg: 'rgba(244,67,54,0.15)', color: '#ef9a9a' },
  success: { bg: 'rgba(76,175,80,0.15)', color: '#81c784' },
  info: { bg: 'rgba(79,195,247,0.15)', color: '#81d4fa' },
  warning: { bg: 'rgba(255,193,7,0.15)', color: '#FFC107' }
};

/**
 * Plain-English names for the fields that turn up in the log.
 *
 * Anything not listed falls back to a prettified version of the raw field name,
 * so a new field still reads sensibly without being added here first.
 */
const FIELD_LABELS = {
  isActive: 'Status',
  comingSoon: 'Coming soon',
  promotionPriority: 'Promotion priority',
  gameSequence: 'Display order',
  gameURL: 'Game link',
  gameSlug: 'Game',
  gameName: 'Game name',
  rtpLevel: 'RTP level',
  gameModeId: 'Game mode',
  betLevels: 'Bet amounts players can choose',
  minBet: 'Minimum bet',
  maxBet: 'Maximum bet',
  stepBet: 'Bet increment',
  defaultBetLevel: 'Default bet',
  paytable: 'Paytable',
  betMultiplier: 'Bet multiplier',
  maxBetCap: 'Maximum bet cap',
  moduleAccess: 'Module access',
  allowedSections: 'Module access',
  operatorKey: 'Secure key',
  displayCode: 'Display currency',
  internalCurrency: 'Internal currency',
  operatorId: 'Operator',
  domains: 'Website addresses',
  domainName: 'Website address',
  isPrimary: 'Primary website',
  publicEmail: 'Email',
  live_version: 'Live version',
  modeCount: 'Number of game modes',
  outcomeCount: 'Number of outcomes',
  code: 'Currency code',
  name: 'Name',
  multiplier: 'Bet multiplier',
  sourceCurrencyCode: 'Source currency',
  gameSlugs: 'Games',
  mappings: 'Currency mapping'
};

const fieldLabel = (field) => FIELD_LABELS[field] || humanize(field);

/**
 * Turn a stored value into words.
 *
 * Booleans read differently depending on the field — "Active / Inactive" for a
 * status, "Yes / No" for a flag — because "Is Active: No" tells a non-technical
 * reader very little.
 */
const describeValue = (field, value, max = 70) => {
  if (value === null || value === undefined || value === '') return 'nothing';

  if (typeof value === 'boolean') {
    if (field === 'isActive') return value ? 'Active' : 'Inactive';
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return 'none';
    const text = value.map((item) => describeValue(field, item, 20)).join(', ');
    return text.length > max ? `${value.length} values (${text.slice(0, max)}…)` : text;
  }

  if (typeof value === 'object') {
    const text = value.name || value.objectId || value.fileName || JSON.stringify(value);
    return text.length > max ? `${text.slice(0, max)}…` : text;
  }

  const text = String(value);
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

/**
 * One line per changed field. Long lists collapse behind a "show more" so a
 * paytable edit does not push every other row off the screen.
 */
const DetailsCell = ({ details }) => {
  const [expanded, setExpanded] = useState(false);

  const changedFields = details?.changedFields || [];
  const payload = details?.payload || {};
  const summary = details?.summary;

  // Rows written before the log was reworked kept their content at the top
  // level of `details` rather than under `payload`.
  const legacyFields = Object.fromEntries(
    Object.entries(details || {}).filter(
      ([key]) => !['summary', 'changedFields', 'payload'].includes(key)
    )
  );

  const fallbackEntries = Object.entries(
    Object.keys(payload).length > 0 ? payload : legacyFields
  ).filter(([, value]) => value !== undefined && value !== null && value !== '');

  const rows =
    changedFields.length > 0
      ? changedFields.map(({ field, from, to }) => ({ field, from, to, isDiff: true }))
      : fallbackEntries.map(([field, value]) => ({ field, to: value, isDiff: false }));

  if (rows.length === 0) {
    return (
      <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)' }}>
        {summary || 'No field changes recorded'}
      </Typography>
    );
  }

  const COLLAPSE_AFTER = 3;
  const visible = expanded ? rows : rows.slice(0, COLLAPSE_AFTER);
  const hidden = rows.length - visible.length;

  // Each change reads as a sentence: "Status changed from Active to Inactive"
  // rather than a field name and two raw values.
  const renderRow = ({ field, from, to, isDiff }) => (
    <Box key={field} sx={{ mb: '4px', fontSize: '12.5px', lineHeight: 1.6 }}>
      <Box component="span" sx={{ color: 'rgba(255,255,255,0.85)' }}>
        {fieldLabel(field)}
      </Box>
      {isDiff ? (
        <>
          <Box component="span" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            {' changed from '}
          </Box>
          <Box component="span" sx={{ color: '#ef9a9a', textDecoration: 'line-through' }}>
            {describeValue(field, from)}
          </Box>
          <Box component="span" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            {' to '}
          </Box>
          <Box component="span" sx={{ color: '#81c784', fontWeight: 600 }}>
            {describeValue(field, to)}
          </Box>
        </>
      ) : (
        <>
          <Box component="span" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            {' set to '}
          </Box>
          <Box component="span" sx={{ color: '#81c784', fontWeight: 600 }}>
            {describeValue(field, to)}
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box>
      {summary && (
        <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', mb: '6px' }}>
          {summary}
        </Typography>
      )}
      {visible.map(renderRow)}
      {rows.length > COLLAPSE_AFTER && (
        <Button
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{
            p: 0,
            minWidth: 0,
            fontSize: '11px',
            textTransform: 'none',
            color: '#FFC107',
            '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
          }}
        >
          {expanded ? 'Show less' : `Show ${hidden} more`}
        </Button>
      )}
    </Box>
  );
};

const HEADERS = ['Date / Time', 'Actor', 'Action', 'Module', 'Target', 'What changed'];

const EMPTY_FILTERS = { actorId: '', section: '', action: '', search: '', from: '', to: '' };

// Dark dropdown menu — the browser's native select list renders white.
const MENU_PROPS = {
  PaperProps: {
    sx: {
      backgroundColor: '#2B2B2B',
      border: '1px solid rgba(255,255,255,0.12)',
      maxHeight: 360,
      '& .MuiMenuItem-root': {
        color: '#fff',
        fontSize: '13px',
        '&:hover': { backgroundColor: 'rgba(255,193,7,0.12)' },
        '&.Mui-selected': {
          backgroundColor: 'rgba(255,193,7,0.2)',
          '&:hover': { backgroundColor: 'rgba(255,193,7,0.28)' }
        }
      }
    }
  }
};

const fieldSx = {
  minWidth: 180,
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#2B2B2B',
    color: '#fff',
    borderRadius: '8px',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#FFC107' }
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#FFC107' },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' },
  '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)', opacity: 0.6 }
};

export const ActivityLogView = ({ description }) => {
  const { data: identity, isLoading } = useGetIdentity();
  const isSuperUser = identity?.role === 'Super-User';

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [options, setOptions] = useState({
    actors: [],
    actions: [],
    sections: [],
    roles: [],
    totalCount: 0
  });

  // Which actor role the current tab shows. '' is the combined view.
  const [roleTab, setRoleTab] = useState('');

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter((value) => value !== '').length,
    [filters]
  );

  const loadOptions = useCallback(() => {
    Parse.Cloud.run('getAdminActivityLogFilters')
      .then((result) =>
        setOptions({
          actors: result?.actors || [],
          actions: result?.actions || [],
          sections: result?.sections || [],
          roles: result?.roles || [],
          totalCount: result?.totalCount || 0
        })
      )
      .catch(() => setOptions({ actors: [], actions: [], sections: [], roles: [], totalCount: 0 }));
  }, []);

  useEffect(() => {
    if (isLoading || !isSuperUser) return;
    loadOptions();
  }, [isLoading, isSuperUser, loadOptions]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // Filtering and paging both happen server-side, so `total` always matches
      // the rows that can actually be paged through.
      const result = await Parse.Cloud.run('getAdminActivityLogs', {
        page,
        limit: perPage,
        actorRole: roleTab || undefined,
        actorId: filters.actorId || undefined,
        section: filters.section || undefined,
        action: filters.action || undefined,
        search: filters.search || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined
      });

      setLogs(result?.logs || []);
      setTotal(result?.total || 0);
    } catch (err) {
      console.error('Failed to load activity logs:', err);
      setError(err.message || 'Failed to load activity logs.');
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filters, roleTab]);

  useEffect(() => {
    if (isLoading || !isSuperUser) {
      setLoading(false);
      return;
    }
    fetchLogs();
  }, [isLoading, isSuperUser, fetchLogs]);

  const applyFilters = () => {
    setPage(1);
    setFilters(draftFilters);
  };

  const clearFilters = () => {
    setPage(1);
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
  };

  const handleRoleTabChange = (_event, value) => {
    setPage(1);
    setRoleTab(value);
  };

  const handleDraftChange = (key) => (event) =>
    setDraftFilters((prev) => ({ ...prev, [key]: event.target.value }));

  if (isLoading) return null;

  if (!isSuperUser) {
    return <Box sx={{ p: 3, color: 'rgba(255,255,255,0.7)' }}>Access denied. Super-User only.</Box>;
  }

  return (
    <Box sx={{ mt: 2, pb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 1.5
        }}
      >
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', maxWidth: '680px' }}>
          {description}
        </Typography>

        <Button
          startIcon={<FilterListIcon />}
          endIcon={showFilters ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          onClick={() => setShowFilters(!showFilters)}
          sx={{
            textTransform: 'none',
            color: activeFilterCount > 0 ? '#FFC107' : 'rgba(255,255,255,0.75)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '8px',
            px: 2
          }}
        >
          Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </Button>
      </Box>

      {/* Role tabs — only roles that have actually recorded something. */}
      <Tabs
        value={roleTab}
        onChange={handleRoleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          minHeight: '42px',
          '& .MuiTab-root': {
            color: 'rgba(255,255,255,0.6)',
            textTransform: 'none',
            fontSize: '14px',
            minHeight: '42px',
            py: 0
          },
          '& .Mui-selected': { color: '#FFC107 !important' },
          '& .MuiTabs-indicator': { backgroundColor: '#FFC107', height: '3px' }
        }}
      >
        <Tab value="" label={`All activity (${options.totalCount})`} />
        {options.roles.map(({ role, count }) => (
          <Tab key={role} value={role} label={`${humanize(role)} (${count})`} />
        ))}
      </Tabs>

      <Collapse in={showFilters}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 2,
            p: 2,
            backgroundColor: '#242424',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '10px'
          }}
        >
          <TextField
            size="small"
            label="Search"
            placeholder="Actor, target or action"
            value={draftFilters.search}
            onChange={handleDraftChange('search')}
            onKeyDown={(event) => event.key === 'Enter' && applyFilters()}
            sx={{ ...fieldSx, minWidth: 220 }}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            select
            size="small"
            label="Actor"
            value={draftFilters.actorId}
            onChange={handleDraftChange('actorId')}
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ MenuProps: MENU_PROPS, displayEmpty: true }}
          >
            <MenuItem value="">All actors</MenuItem>
            {options.actors.map((actor) => (
              <MenuItem key={actor.id} value={actor.id}>
                {actor.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Module"
            value={draftFilters.section}
            onChange={handleDraftChange('section')}
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ MenuProps: MENU_PROPS, displayEmpty: true }}
          >
            <MenuItem value="">All modules</MenuItem>
            {options.sections.map((section) => (
              <MenuItem key={section.key} value={section.key}>
                {section.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Action"
            value={draftFilters.action}
            onChange={handleDraftChange('action')}
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ MenuProps: MENU_PROPS, displayEmpty: true }}
          >
            <MenuItem value="">All actions</MenuItem>
            {options.actions.map((action) => (
              <MenuItem key={action} value={action}>
                {humanize(action)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            type="date"
            label="From"
            value={draftFilters.from}
            onChange={handleDraftChange('from')}
            InputLabelProps={{ shrink: true }}
            sx={{ ...fieldSx, minWidth: 160 }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={draftFilters.to}
            onChange={handleDraftChange('to')}
            InputLabelProps={{ shrink: true }}
            sx={{ ...fieldSx, minWidth: 160 }}
          />

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="contained"
              onClick={applyFilters}
              sx={{
                backgroundColor: '#FFC107',
                color: '#1A1A1A',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': { backgroundColor: '#FFD54F' }
              }}
            >
              Apply
            </Button>
            {activeFilterCount > 0 && (
              <Button
                variant="text"
                onClick={clearFilters}
                sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>
      </Collapse>

      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: '#242424',
          borderRadius: '10px',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: 'none',
          overflowX: 'auto'
        }}
      >
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              {HEADERS.map((header) => (
                <TableCell
                  key={header}
                  sx={{
                    color: '#FFC107',
                    fontWeight: 600,
                    fontSize: '13px',
                    backgroundColor: 'rgba(255,255,255,0.02)',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    whiteSpace: 'nowrap',
                    py: 1.5
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 6, border: 'none' }}>
                  <CircularProgress sx={{ color: '#FFC107' }} />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell
                  colSpan={HEADERS.length}
                  align="center"
                  sx={{ py: 6, color: '#ef9a9a', border: 'none' }}
                >
                  {error}
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={HEADERS.length}
                  align="center"
                  sx={{ py: 6, color: 'rgba(255,255,255,0.5)', border: 'none' }}
                >
                  {activeFilterCount > 0
                    ? 'No activity matches these filters.'
                    : 'No activity recorded yet.'}
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => {
                const verb = actionVerb(log.action);
                const tone = TONES[verb.tone];
                const cellSx = {
                  color: '#fff',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                  verticalAlign: 'top',
                  py: 1.5
                };
                const date = log.createdAt ? new Date(log.createdAt) : null;

                return (
                  <TableRow
                    key={log.id}
                    sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}
                  >
                    <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                      {date ? (
                        <>
                          <Typography sx={{ fontSize: '13px' }}>
                            {date.toLocaleDateString()}
                          </Typography>
                          <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)' }}>
                            {date.toLocaleTimeString()}
                          </Typography>
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                        {log.actorName || log.actorId || 'Unknown'}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)' }}>
                        {log.actorRole || 'Role not recorded'}
                      </Typography>
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Tooltip title={humanize(log.action)} arrow>
                        <Chip
                          label={verb.label}
                          size="small"
                          sx={{
                            backgroundColor: tone.bg,
                            color: tone.color,
                            fontSize: '11px',
                            fontWeight: 600,
                            height: '22px'
                          }}
                        />
                      </Tooltip>
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: '13px' }}>
                        {log.sectionLabel || humanize(log.section) || 'Not recorded'}
                      </Typography>
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: '13px' }}>
                        {log.targetName || log.targetId || 'Not recorded'}
                      </Typography>
                      {log.targetType && (
                        <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)' }}>
                          {humanize(String(log.targetType).replace(/^_/, ''))}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ ...cellSx, minWidth: '280px', maxWidth: '420px' }}>
                      <DetailsCell details={log.details} />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Shown whenever there is anything to page through, so a filter that
            lands on an empty page still offers a way back. */}
        {!loading && total > 0 && (
          <CustomPagination
            page={page}
            perPage={perPage}
            total={total}
            setPage={setPage}
            setPerPage={setPerPage}
          />
        )}
      </TableContainer>
    </Box>
  );
};

export default ActivityLogView;
